import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { axPerfLogger } from '@theme/performance';

/**
 * Cart Drawer Upsell Component
 *
 * Renders product recommendations as a Swiper carousel inside the cart drawer.
 *
 * - Dynamic mode: fetches recommendations from Shopify's Product Recommendations
 *   API via Section Rendering. Falls back to a curated collection.
 * - Collection mode: server-rendered only, JS still inits Swiper for the slider UI.
 *
 * The SSR fallback (`[data-ax-fallback]`) renders raw product-cards. On init,
 * we move them into `<swiper-slide>` elements inside the `<swiper-container>`,
 * then call `.initialize()` programmatically (same pattern as ax-tabbed-product-lists).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} productsContainer
 * @property {HTMLElement & { swiper?: any, initialize?: () => void }} swiper
 * @property {HTMLElement} fallback
 * @property {HTMLElement} arrows
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
class CartDrawerUpsell extends Component {
  /** @type {string} */
  #currentProductId = '';

  /** @type {boolean} */
  #hasFetchedInitial = false;

  /** @type {boolean} */
  #swiperReady = false;

  connectedCallback() {
    super.connectedCallback();

    this.#currentProductId = this.dataset.productId || '';
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    // Init Swiper from whatever SSR fallback rendered (collection mode, recommendations
    // already performed on this page, etc.). Empty in dynamic-mode initial render —
    // we'll re-init after the fetch fills the fallback.
    this.#initSwiper();

    if (Shopify.designMode) {
      if (this.#currentProductId) {
        this.#hasFetchedInitial = true;
        this.#fetchAndRender(this.#currentProductId);
      }
    } else if (this.#currentProductId) {
      this.#hasFetchedInitial = true;
      this.#fetchAndRender(this.#currentProductId);
    } else {
      document.addEventListener('ax:cart-drawer-opened', this.#handleDrawerOpen, { once: true });
    }
  }

  updatedCallback() {
    super.updatedCallback();

    const newProductId = this.dataset.productId || '';
    if (!newProductId) return;

    if (newProductId !== this.#currentProductId) {
      this.#currentProductId = newProductId;
      this.#fetchAndRender(newProductId);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener('ax:cart-drawer-opened', this.#handleDrawerOpen);
    this.#destroySwiper();
  }

  #handleDrawerOpen = () => {
    if (this.#hasFetchedInitial || !this.#currentProductId) return;
    this.#hasFetchedInitial = true;
    this.#fetchAndRender(this.#currentProductId);
  };

  /**
   * @param {Event} event
   */
  #handleCartUpdate = (event) => {
    const detail =
      /** @type {{ detail?: { data?: { productId?: string, source?: string, didError?: boolean } } }} */
      (event);

    if (detail.detail?.data?.didError) return;

    const addedProductId = detail.detail?.data?.productId;
    if (!addedProductId) return;

    const newId = addedProductId.toString();
    if (newId !== this.#currentProductId) {
      this.#currentProductId = newId;
      this.#fetchAndRender(newId);
    }
  };

  /**
   * Fetches recommendations via Section Rendering and re-renders slides + Swiper.
   *
   * `intent=complementary` returns *only* the products curated in Search &
   * Discovery, and an empty list for products with no curation. So when the
   * primary intent yields no slide we retry once with `related` rather than
   * leaving the block empty.
   *
   * @param {string} productId
   */
  async #fetchAndRender(productId) {
    const baseUrl = this.dataset.url;
    const sectionId = this.dataset.sectionId;
    if (!baseUrl || !productId || !sectionId) return;

    const intent = this.dataset.intent || 'complementary';
    const perfOp = axPerfLogger.start('upsell-fetch', { productId, intent });

    try {
      perfOp.mark('fetch-start');
      let slides = await this.#fetchSlides(baseUrl, productId, sectionId, intent);
      perfOp.mark('html-parsed');

      if (slides.length === 0 && intent === 'complementary') {
        slides = await this.#fetchSlides(baseUrl, productId, sectionId, 'related');
        perfOp.mark('fallback-parsed');
      }

      if (slides.length === 0) return;

      this.#replaceSlides(slides);
      perfOp.mark('slides-replaced');
    } catch {
      // Keep current state
    } finally {
      perfOp.end();
    }
  }

  /**
   * Requests one recommendations intent and extracts the rendered slides.
   * @param {string} baseUrl
   * @param {string} productId
   * @param {string} sectionId
   * @param {string} intent
   * @returns {Promise<HTMLElement[]>}
   */
  async #fetchSlides(baseUrl, productId, sectionId, intent) {
    const url = `${baseUrl}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;
    const response = await fetch(url);
    if (!response.ok) return [];

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');

    return /** @type {HTMLElement[]} */ ([
      ...doc.querySelectorAll('.cart-drawer-upsell__fallback .cart-drawer-upsell__slide'),
    ]);
  }

  /**
   * Replaces existing slides with fresh ones. Reuses the existing Swiper
   * instance via removeAllSlides() + appendSlide() so the carousel config
   * (slidesPerView, spaceBetween, peek) survives the refresh.
   * If Swiper isn't initialised yet (dynamic mode first fetch), bootstrap via
   * the fallback path instead.
   * @param {HTMLElement[]} newSlides
   */
  #replaceSlides(newSlides) {
    const swiper = this.#getSwiper();

    if (!swiper) {
      // First fetch in dynamic mode — populate the fallback then init Swiper.
      const fallback = this.querySelector('[data-ax-fallback]');
      if (fallback instanceof HTMLElement) {
        fallback.replaceChildren(...newSlides);
        this.#initSwiper();
      }
      return;
    }

    // Wrap each raw .cart-drawer-upsell__slide in <swiper-slide> for Swiper API
    const wrapped = newSlides.map((slide) => {
      const ss = document.createElement('swiper-slide');
      ss.appendChild(slide);
      return ss;
    });

    swiper.removeAllSlides();
    swiper.appendSlide(wrapped);
    swiper.update();

    this.#syncArrowsVisibility(wrapped.length);
    this.#syncArrowsState();
  }

  /**
   * Wraps SSR fallback slides in <swiper-slide>, configures Swiper, and initializes it.
   */
  async #initSwiper() {
    if (this.#swiperReady) return;

    const swiperEl = this.querySelector('swiper-container.cart-drawer-upsell__swiper');
    const fallback = this.querySelector('[data-ax-fallback]');
    if (!(swiperEl instanceof HTMLElement) || !(fallback instanceof HTMLElement)) return;

    const rawSlides = [...fallback.children];
    if (rawSlides.length === 0) {
      // No slides yet (dynamic mode, no fetch performed) — hide arrows + leave Swiper
      // un-initialised. We'll re-enter this method after #fetchAndRender finishes.
      this.#syncArrowsVisibility(0);
      return;
    }

    await customElements.whenDefined('swiper-container');

    for (const slide of rawSlides) {
      const swiperSlide = document.createElement('swiper-slide');
      swiperSlide.appendChild(slide);
      swiperEl.appendChild(swiperSlide);
    }
    fallback.remove();

    const swiperContainer =
      /** @type {HTMLElement & { swiper?: any, initialize?: () => void }} */
      (swiperEl);

    Object.assign(swiperContainer, {
      // Mobile default: 1.4 → 1 card + ~40% of the next peeking on 375px width.
      // Desktop (>=750px): 2.3 → 2 cards + ~30% peek inside the 480px drawer.
      slidesPerView: 1.4,
      slidesPerGroup: 1,
      spaceBetween: 12,
      speed: 400,
      grabCursor: true,
      watchOverflow: true,
      a11y: true,
      keyboard: { enabled: true },
      breakpoints: {
        750: { slidesPerView: 2.3, spaceBetween: 12 },
      },
    });

    swiperContainer.initialize?.();
    swiperContainer.removeAttribute('init');
    this.#swiperReady = true;

    const swiper = swiperContainer.swiper;
    if (!swiper) return;

    this.#bindArrows(swiper);
    this.#syncArrowsVisibility(rawSlides.length);
  }

  /**
   * on:click handler for the "previous" slideshow-arrow.
   */
  previous() {
    this.#getSwiper()?.slidePrev();
  }

  /**
   * on:click handler for the "next" slideshow-arrow.
   */
  next() {
    this.#getSwiper()?.slideNext();
  }

  #getSwiper() {
    const swiperEl =
      /** @type {(HTMLElement & { swiper?: any }) | null} */
      (this.querySelector('swiper-container.cart-drawer-upsell__swiper'));
    return swiperEl?.swiper ?? null;
  }

  /**
   * Binds disabled-state sync between Swiper and the slideshow-arrow refs.
   * @param {any} swiper
   */
  #bindArrows(swiper) {
    this.#syncArrowsState();

    for (const event of ['slideChange', 'reachBeginning', 'reachEnd', 'fromEdge', 'resize']) {
      swiper.on(event, this.#syncArrowsState);
    }
  }

  /**
   * Pushes the current Swiper position onto the arrows' disabled state and the mask.
   *
   * Swiper only emits its position events on user-driven moves, so a programmatic
   * refresh of the slide set leaves the arrows frozen: `removeAllSlides()` empties
   * the carousel, which disables "next", and `update()` emits none of the bound
   * events once the new slides land. Any programmatic change to the slides must
   * therefore call this explicitly, on top of the event bindings.
   * @private
   */
  #syncArrowsState = () => {
    const swiper = this.#getSwiper();
    if (!swiper) return;

    const { previous, next } = /** @type {Refs} */ (this.refs);
    if (previous) previous.disabled = Boolean(swiper.isBeginning);
    if (next) next.disabled = Boolean(swiper.isEnd);
    this.#syncMask(swiper);
  };

  /**
   * Updates the CSS mask on the products container based on swiper position.
   * Fades the left edge when scrolled past the start, fades the right edge
   * when not yet at the end — providing a visual hint for more content.
   * @param {any} swiper
   */
  #syncMask(swiper) {
    const container = /** @type {HTMLElement | undefined} */ (this.refs.productsContainer);
    if (!container) return;

    const atStart = Boolean(swiper.isBeginning);
    const atEnd = Boolean(swiper.isEnd);

    if (atStart && atEnd) {
      container.style.maskImage = 'none';
    } else if (atStart) {
      container.style.maskImage = 'linear-gradient(to right, black 90%, transparent 100%)';
    } else if (atEnd) {
      container.style.maskImage = 'linear-gradient(to right, transparent 0%, black 10%)';
    } else {
      container.style.maskImage = 'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)';
    }
  }

  /**
   * Hides the arrows when there aren't enough slides to slide.
   * @param {number} slideCount
   */
  #syncArrowsVisibility(slideCount) {
    const arrows = /** @type {HTMLElement | undefined} */ (this.refs.arrows);
    if (!arrows) return;
    // 2 slides visible → arrows useful only when 3+ products
    arrows.hidden = slideCount < 3;
  }

  #destroySwiper() {
    const swiperEl =
      /** @type {(HTMLElement & { swiper?: any }) | null} */
      (this.querySelector('swiper-container.cart-drawer-upsell__swiper'));
    swiperEl?.swiper?.destroy(true, true);
    this.#swiperReady = false;
  }
}

if (!customElements.get('cart-drawer-upsell')) {
  customElements.define('cart-drawer-upsell', CartDrawerUpsell);
}
